/**
 * `debouncedRecomputeView` — a shared ProseMirror plugin `view` that turns an
 * expensive full-document decorator re-scan into a debounced operation (perf,
 * 2026-06-10).
 *
 * The hashtag + entity decorators each derive a `DecorationSet` by walking every
 * text node in the document. Doing that synchronously on every keystroke is O(doc)
 * per character and is a primary cause of editor lag on large notes / big tables.
 *
 * The fix is the standard ProseMirror pattern:
 *   - In the plugin's `state.apply`, on an ordinary doc-changing transaction, just
 *     `DecorationSet.map(tr.mapping, tr.doc)` the existing set — cheap, O(changes).
 *     Existing chips shift with the text; no full scan.
 *   - This `view` watches for doc changes and, a short debounce after the user
 *     stops typing, dispatches ONE transaction carrying `{ recompute: true }` meta
 *     under the plugin's key. The `apply` does the full re-scan only when it sees
 *     that meta — so newly typed `#tags` / entity mentions get decorated ~one
 *     debounce-window after you finish typing, without ever stalling the keystroke.
 */

import type { PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';

/** Meta payload the debounced view dispatches; the plugin's `apply` recomputes on it. */
export interface RecomputeMeta {
  recompute: true;
}

/** Debounce window for the full re-scan after the last edit. */
export const DECORATOR_RECOMPUTE_DEBOUNCE_MS = 280;

/**
 * Build a Plugin `view` factory that schedules a debounced `{ recompute: true }`
 * meta transaction under `key` whenever the document changes.
 */
export function debouncedRecomputeView(key: PluginKey) {
  return (initialView: EditorView) => {
    let timer: number | null = null;
    let lastDoc = initialView.state.doc;
    const clear = (): void => {
      if (timer !== null) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
    return {
      update(view: EditorView): void {
        if (view.state.doc === lastDoc) return; // selection-only / meta-only change
        lastDoc = view.state.doc;
        clear();
        timer = window.setTimeout(() => {
          timer = null;
          const meta: RecomputeMeta = { recompute: true };
          view.dispatch(view.state.tr.setMeta(key, meta));
        }, DECORATOR_RECOMPUTE_DEBOUNCE_MS);
      },
      destroy(): void {
        clear();
      },
    };
  };
}
